import React, {
  type FC,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  LuArrowUp,
  LuChevronLeft,
  LuDownload,
  LuFileText,
  LuHeadset,
  LuPaperclip,
  LuStar,
  LuUserRoundCheck,
  LuX,
} from "react-icons/lu";
import { toast } from "react-toastify";
import { useI18next } from "gatsby-plugin-react-i18next";

import {
  CHAT_BRAND,
  CHAT_CHIP,
  CHAT_ICON_BUTTON,
  CHAT_TOPICS,
  customerTitleOf,
  formatDay,
  formatTime,
  isClosed,
  nameFrom,
  quickRepliesOf,
  savedTitleOf,
  type Sender,
  senderOf,
  splitSuggestions,
} from "@components/chat/chat_helpers";
import { renderChatMarkdown } from "@components/chat/chat_markdown";
import ChatTicketBanner from "@components/chat/chat_ticket_banner";
import { useAccountDetailStore } from "@stores/account_detail";
import { pollThread, useCustomerCareStore } from "@stores/customer_care";
import {
  type CcAttachment,
  ccAttachmentUrl,
  type CcMessage,
  isImageAttachment,
} from "@utilities/customer-care";
import { cn } from "@utilities/helper";

type Group =
  | { kind: "them"; sender: Sender; items: CcMessage[] }
  | { kind: "me"; items: CcMessage[] }
  | { kind: "system"; id: string; text: string };

/**
 * The conversation view (Figma "Chat Thread Conversation"): messages grouped
 * by sender, the other side's label and avatar once per group, mine on the
 * end side in the primary colour, only the latest message stamped, typing dots from the
 * socket, attachments as cards or inline images, the rating card once the
 * conversation is closed.
 */
const ChatThread: FC<{
  titleId: string;
  /** `null` = a draft: nothing exists on the server until the first send. */
  id: string | null;
  onCreated?: (id: string) => void;
  onBack: () => void;
}> = ({ titleId, id, onCreated, onBack }) => {
  const { t, language: locale } = useI18next();
  const store = useCustomerCareStore();
  const thread = id === null ? undefined : store.threads[id];
  const conversation =
    id === null
      ? undefined
      : store.conversations.find(c => String(c.id) === id);
  const typing = id === null ? undefined : store.typing[id];
  const [isLoading, setLoading] = useState(id !== null && thread === undefined);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<CcAttachment | null>(null);
  const [isUploading, setUploading] = useState(false);
  const [isSending, setSending] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load + join the room on open; leave it on close.
  useEffect(() => {
    if (id === null) return;
    let cancelled = false;
    store.setFocused(id);
    if (thread === undefined) {
      store
        .openThread(id)
        .catch(error => {
          console.error("[customer care] open thread failed:", error);
          if (!cancelled) {
            toast.error(t("chat_not_found"), { autoClose: 4000 });
            onBack();
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
      store.closeThread(id);
      store.setFocused(null);
    };
  }, [id]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [isLoading]);

  // The thread polls the conversation every 4 s while open, socket or not
  // (see pollThread), and right after a send to catch the bot's reply.
  useEffect(() => {
    if (id === null) return;
    const timer = window.setInterval(() => {
      void pollThread(id);
    }, 4000);
    return () => {
      window.clearInterval(timer);
    };
  }, [id]);
  const pollSoon = (target: string): void => {
    for (const delay of [1200, 2500, 5000]) {
      window.setTimeout(() => {
        void pollThread(target);
      }, delay);
    }
  };
  /** Sends into the conversation, creating it first for a draft. */
  const deliver = async (
    text: string,
    attachments: CcAttachment[],
    title?: string,
  ): Promise<void> => {
    if (id !== null) {
      await store.send(id, text, attachments);
      pollSoon(id);
      return;
    }
    const created = await store.startConversation(title);
    await store.send(created, text, attachments);
    pollSoon(created);
    onCreated?.(created);
  };

  // The typing indicator expires on its own (a missed typing:stop): the
  // clock state advances when the current indicator runs out.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (typing === undefined) return;
    const timer = window.setTimeout(
      () => {
        setNow(Date.now());
      },
      Math.max(0, typing.until - Date.now()),
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [typing]);
  const isTyping = typing !== undefined && typing.until > now;

  const messages = thread?.messages ?? [];
  // Opening a chat lands on its latest message; what arrives after that
  // glides into view (a jump under reduced motion).
  const hasLanded = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return;
    const glide =
      hasLanded.current &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: el.scrollHeight, behavior: glide ? "smooth" : "auto" });
    if (messages.length > 0) hasLanded.current = true;
  }, [messages.length, isTyping]);

  const profileId = store.profile?.userId;
  const groups: Group[] = [];
  let sawAgent = false;
  for (const message of messages) {
    const sender = senderOf(
      message,
      thread,
      profileId,
      t("chat_support_team", CHAT_BRAND),
    );
    if (!sender.mine && !sender.isBot && !sawAgent) {
      sawAgent = true;
      groups.push({
        kind: "system",
        id: `joined-${message.id}`,
        text: t("chat_agent_joined", { name: sender.name }),
      });
    }
    const last = groups[groups.length - 1];
    if (sender.mine) {
      if (last?.kind === "me") last.items.push(message);
      else groups.push({ kind: "me", items: [message] });
    } else if (last?.kind === "them" && last.sender.name === sender.name) {
      last.items.push(message);
    } else {
      groups.push({ kind: "them", sender, items: [message] });
    }
  }
  const lastGroup = groups[groups.length - 1];
  const firstMessage: CcMessage | undefined = messages[0];

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const text = draft.trim();
    if ((text === "" && pending === null) || isSending || isUploading) return;
    setSending(true);
    try {
      // A typed first message titles the draft's conversation, as a topic
      // bubble does, so the list card reads by topic: a common subject's
      // title ("Is there a way to get refund" → "Refund") or the tidied first
      // sentence, no title when only a greeting is left.
      await deliver(
        text,
        pending !== null ? [pending] : [],
        id === null ? nameFrom(text, locale, t) : undefined,
      );
      setDraft("");
      setPending(null);
    } catch (error) {
      console.error("[customer care] send failed:", error);
      toast.error(t("chat_send_failed"), { autoClose: 4000 });
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const pickFile = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setUploading(true);
    try {
      const url = await store.upload(file);
      setPending([file.name, url]);
    } catch (error) {
      console.error("[customer care] upload failed:", error);
      toast.error(t("chat_upload_failed"), { autoClose: 4000 });
    } finally {
      setUploading(false);
      if (fileRef.current !== null) fileRef.current.value = "";
    }
  };

  const discardPending = (): void => {
    if (pending !== null) void store.discardUpload(pending[1]);
    setPending(null);
  };

  const rated = id === null ? undefined : store.rated[id];
  const showRating = isClosed(conversation) && rated === undefined;
  // Reply choices belong to the latest message only, and only while it is
  // the other side's turn.
  const lastMessage = messages[messages.length - 1];
  // A fresh conversation ("Send a message") opens with a greeting and offers
  // the popular topics as bubbles, the same ones the chat button suggests on
  // hover. The greeting is shown here only, never sent: once the customer
  // writes, the bot greets for itself.
  const isFresh = !isLoading && messages.length === 0;
  const firstName = useAccountDetailStore(
    state => state.license?.name?.trim().split(/\s+/)[0] ?? "",
  );
  const replies = isFresh
    ? CHAT_TOPICS.map(topic => t(topic.key))
    : lastMessage !== undefined &&
        String(lastMessage.userId) !== String(profileId ?? "") &&
        !isTyping
      ? [
          ...splitSuggestions(lastMessage.message).suggestions,
          ...quickRepliesOf(lastMessage.payload),
        ].filter((reply, index, all) => all.indexOf(reply) === index)
      : [];
  const sendReply = async (text: string): Promise<void> => {
    if (isSending) return;
    setSending(true);
    try {
      // A topic bubble on a draft names the conversation after the topic.
      await deliver(text, [], id === null ? text : undefined);
    } catch (error) {
      console.error("[customer care] send failed:", error);
      toast.error(t("chat_send_failed"), { autoClose: 4000 });
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };
  const lit = rated ?? hoverStar;

  return (
    <>
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-black/10 bg-white px-4">
        <button
          type="button"
          aria-label={t("back")}
          onClick={onBack}
          className={CHAT_ICON_BUTTON}
        >
          <LuChevronLeft aria-hidden="true" className="rtl:-scale-x-100" />
        </button>
        <h2
          id={titleId}
          className="mb-0 min-w-px flex-1 truncate text-base font-bold text-black"
        >
          {/* The same name as the chat's card in the Chat history. */}
          {[
            savedTitleOf(conversation?.title, locale, t),
            customerTitleOf(messages, profileId, locale, t),
          ].find(title => title !== "") ??
            t("chat_new_conversation", CHAT_BRAND)}
        </h2>
      </div>

      {/* Wait compensation (24 h) and management escalation (48 h), from
          pts; Ben's confirmation lands in the thread, so poll for it. */}
      {id !== null && (
        <ChatTicketBanner
          conversationId={id}
          onDone={() => {
            pollSoon(id);
          }}
        />
      )}

      <div
        ref={scrollRef}
        role="log"
        aria-label={t("chat_conversation")}
        className="min-h-px flex-1 overflow-y-auto overscroll-contain bg-primary-100/60"
      >
        {isLoading ? null : (
          <div className="flex min-h-full flex-col justify-end gap-4 p-5">
            {firstMessage !== undefined && (
              <p className="mb-0 text-center text-xs text-black/60">
                {formatDay(firstMessage.creationTime, locale)}
              </p>
            )}
            {isFresh && (
              <div className="chat-in flex items-end gap-1.5">
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  <LuHeadset className="size-4" aria-hidden="true" />
                </span>
                <div className="flex min-w-px flex-col items-start gap-1.5">
                  <p className="mb-0 text-xs font-bold text-black/60">
                    <span className="sr-only">{t("chat_from")} </span>
                    {t("chat_support_team", CHAT_BRAND)}
                  </p>
                  <p className="mb-0 max-w-[300px] rounded-2xl rounded-es-none bg-white p-3 text-sm text-black">
                    {firstName !== ""
                      ? t("chat_greeting_name", {
                          ...CHAT_BRAND,
                          name: firstName,
                        })
                      : t("chat_greeting", CHAT_BRAND)}
                  </p>
                </div>
              </div>
            )}
            {groups.map((group, index) => {
              if (group.kind === "system") {
                return (
                  <p
                    key={group.id}
                    className="chat-in mb-0 flex items-center justify-center gap-1.5 py-1.5 text-xs text-black/60"
                  >
                    <LuUserRoundCheck
                      aria-hidden="true"
                      className="size-3.5 shrink-0"
                    />
                    {group.text}
                  </p>
                );
              }
              const last = group.items[group.items.length - 1];
              const stamp =
                index === groups.length - 1 && !isTyping && lastGroup === group
                  ? formatTime(last.creationTime, locale)
                  : undefined;
              return group.kind === "them" ? (
                <div
                  key={String(group.items[0].id)}
                  className="flex flex-col gap-1.5"
                >
                  <div className="flex items-end gap-1.5">
                    <Avatar sender={group.sender} />
                    <div className="flex min-w-px flex-col items-start gap-1.5">
                      <p className="mb-0 text-xs font-bold text-black/60">
                        <span className="sr-only">{t("chat_from")} </span>
                        {group.sender.isBot
                          ? t("chat_ai_assistant", { name: group.sender.name })
                          : group.sender.name}
                      </p>
                      {group.items.map((item, i) => (
                        <Bubble
                          key={String(item.id)}
                          message={item}
                          mine={false}
                          tail={i === group.items.length - 1}
                        />
                      ))}
                    </div>
                  </div>
                  {stamp !== undefined && (
                    <Stamp text={stamp} className="ps-[38px]" />
                  )}
                </div>
              ) : (
                <div
                  key={String(group.items[0].id)}
                  className="flex flex-col items-end gap-1.5"
                >
                  {group.items.map((item, i) => (
                    <Bubble
                      key={String(item.id)}
                      message={item}
                      mine
                      tail={i === group.items.length - 1}
                    />
                  ))}
                  {stamp !== undefined && <Stamp text={stamp} />}
                </div>
              );
            })}

            {isTyping && (
              <div className="chat-in flex items-end gap-1.5">
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary/15 text-xs font-semibold text-black"
                >
                  {typing.name.charAt(0).toUpperCase()}
                </span>
                <span className="rounded-2xl rounded-es-none bg-white p-3">
                  <span
                    aria-hidden="true"
                    className="flex h-5 items-center gap-1"
                  >
                    <span className="chat-typing-dot size-1.5 rounded-full bg-primary" />
                    <span className="chat-typing-dot size-1.5 rounded-full bg-primary" />
                    <span className="chat-typing-dot size-1.5 rounded-full bg-primary" />
                  </span>
                </span>
                <span className="sr-only">
                  {t("chat_is_typing", { name: typing.name })}
                </span>
              </div>
            )}

            {replies.length > 0 && (
              <div className="chat-in flex flex-col items-end gap-2">
                {replies.map(reply => (
                  <button
                    key={reply}
                    type="button"
                    disabled={isSending}
                    onClick={() => {
                      void sendReply(reply);
                    }}
                    className={CHAT_CHIP}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            {rated !== undefined && (
              <div className="chat-in flex max-w-[300px] flex-col gap-1.5 self-end rounded-2xl rounded-ee-none bg-primary p-3 text-white">
                <p className="mb-0 text-sm">{t("chat_rated")}</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <LuStar
                      key={star}
                      aria-hidden="true"
                      className={cn(
                        "size-4 fill-current text-white",
                        star > rated && "opacity-40",
                      )}
                    />
                  ))}
                  <span className="sr-only">
                    {t("chat_stars", { count: rated })}
                  </span>
                </div>
              </div>
            )}

            {showRating && (
              <div className="chat-in flex w-full flex-col items-center gap-2 rounded-2xl border border-gray-200 bg-white p-4">
                <p
                  id="chat-rating-title"
                  className="mb-0 text-base font-bold text-black"
                >
                  {t("chat_rating_title")}
                </p>
                <div
                  role="group"
                  aria-labelledby="chat-rating-title"
                  className="flex items-center"
                  onMouseLeave={() => {
                    setHoverStar(0);
                  }}
                >
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      aria-label={t("chat_stars", { count: star })}
                      onMouseEnter={() => {
                        setHoverStar(star);
                      }}
                      onFocus={() => {
                        setHoverStar(star);
                      }}
                      onBlur={() => {
                        setHoverStar(0);
                      }}
                      onClick={() => {
                        if (id === null) return;
                        store.rate(id, star).then(
                          () =>
                            toast.success(t("chat_rated_toast"), {
                              autoClose: 3000,
                            }),
                          () =>
                            toast.error(t("try_again_later"), {
                              autoClose: 4000,
                            }),
                        );
                      }}
                      className="flex size-11 items-center justify-center rounded-full transition-transform duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 motion-safe:hover:scale-110"
                    >
                      <LuStar
                        aria-hidden="true"
                        className={cn(
                          "size-7 fill-current transition-colors duration-150 ease-out",
                          star <= lit ? "text-primary" : "text-black/20",
                        )}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {pending !== null && (
        <div className="flex shrink-0 items-center gap-2 border-t border-black/10 bg-white px-5 py-1">
          <LuFileText
            aria-hidden="true"
            className="size-4 shrink-0 text-black/60"
          />
          <span className="min-w-px flex-1 truncate text-xs text-black/60">
            {pending[0]}
          </span>
          <button
            type="button"
            aria-label={t("remove")}
            onClick={discardPending}
            className={CHAT_ICON_BUTTON}
          >
            <LuX aria-hidden="true" />
          </button>
        </div>
      )}

      <form
        onSubmit={event => {
          void submit(event);
        }}
        className={cn(
          "flex h-16 shrink-0 items-center gap-3 bg-white px-5",
          pending === null && "border-t border-black/10",
        )}
      >
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={event => {
            void pickFile(event.target.files?.[0]);
          }}
        />
        <button
          type="button"
          aria-label={t("chat_attach")}
          disabled={isUploading || isLoading}
          onClick={() => fileRef.current?.click()}
          className={CHAT_ICON_BUTTON}
        >
          <LuPaperclip
            aria-hidden="true"
            className={cn(isUploading && "animate-pulse")}
          />
        </button>
        <input
          ref={inputRef}
          value={draft}
          onChange={event => {
            setDraft(event.target.value);
            if (id !== null && event.target.value.trim() !== "") {
              store.emitTyping(id);
            }
          }}
          placeholder={t("chat_write_message")}
          aria-label={t("chat_message")}
          disabled={isLoading}
          // 16px on phones: iPhone Safari zooms into smaller fields on tap.
          className="min-w-px flex-1 border-0 bg-transparent text-sm text-black outline-none placeholder:text-black/40 focus:ring-0 max-md:text-base"
        />
        {/* Always shown (disabled while empty) so the composer row never
            shifts as the customer starts typing. */}
        <button
          type="submit"
          aria-label={t("chat_send")}
          disabled={
            (draft.trim() === "" && pending === null) ||
            isSending ||
            isUploading
          }
          // 36px button, 44px touch area around it.
          className="relative flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F97C50] to-[#FCBC65] text-white transition after:absolute after:-inset-1 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 active:scale-95 disabled:bg-black/10 disabled:bg-none disabled:text-black/40"
        >
          <LuArrowUp className="size-5" aria-hidden="true" />
        </button>
      </form>
    </>
  );
};

const Avatar: FC<{ sender: Sender }> = ({ sender }) =>
  sender.image !== undefined ? (
    <img
      src={sender.image}
      alt=""
      aria-hidden="true"
      className="size-8 shrink-0 rounded-full object-cover"
    />
  ) : (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        sender.tone,
      )}
    >
      {sender.initial}
    </span>
  );

const Stamp: FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => <p className={cn("mb-0 text-xs text-black/60", className)}>{text}</p>;

/** A text bubble, or the attachment cards / inline images the message carries. */
const Bubble: FC<{ message: CcMessage; mine: boolean; tail: boolean }> = ({
  message,
  mine,
  tail,
}) => {
  const { t } = useI18next();
  const shape = cn(
    "chat-in max-w-[300px] rounded-2xl p-3 text-sm",
    mine ? "bg-primary text-white" : "bg-white text-black",
    tail && (mine ? "rounded-ee-none" : "rounded-es-none"),
  );
  const attachments = message.attachments ?? [];
  // The bot's inline "[choice]" segments become chips (ChatThread); the
  // bubble shows the text alone. A customer's message is shown as typed.
  const text = mine ? message.message : splitSuggestions(message.message).text;
  return (
    <>
      {text.trim() !== "" && (
        <p className={cn(shape, "mb-0 whitespace-pre-line break-words")}>
          {mine && (
            <span className="sr-only">
              {t("chat_you")}
              {": "}
            </span>
          )}
          {mine ? text : renderChatMarkdown(text)}
        </p>
      )}
      {attachments.map(([name, url]) => {
        const href = ccAttachmentUrl(url);
        return isImageAttachment(name) ? (
          <a
            key={url}
            href={href}
            target="_blank"
            rel="noreferrer"
            className={cn(shape, "block overflow-clip p-0")}
          >
            <img
              src={href}
              alt={name}
              className="block max-h-60 w-full object-cover"
            />
          </a>
        ) : (
          <a
            key={url}
            href={href}
            target="_blank"
            rel="noreferrer"
            className={cn(
              shape,
              "flex w-[300px] items-center gap-1.5 no-underline",
            )}
          >
            <span className="flex min-w-px flex-1 items-center gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-black/5">
                <LuFileText
                  aria-hidden="true"
                  className="size-5 text-black/60"
                />
              </span>
              <span className="flex min-w-px flex-1 flex-col gap-0.5">
                <span
                  className={cn(
                    "truncate text-sm font-bold",
                    mine ? "text-white" : "text-black",
                  )}
                >
                  {name}
                </span>
                <span
                  className={cn(
                    "text-xs",
                    mine ? "text-white/80" : "text-black/60",
                  )}
                >
                  {t("chat_download")}
                </span>
              </span>
            </span>
            <LuDownload
              aria-hidden="true"
              className={cn(
                "size-4 shrink-0",
                mine ? "text-white" : "text-black/60",
              )}
            />
          </a>
        );
      })}
    </>
  );
};

export default ChatThread;
